import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, Send, LayoutGrid, Receipt, FileDown, ClipboardList,
  Building2, UserCircle, ChevronDown, ChevronUp, FileText, CheckSquare, Eye, EyeOff, AlertTriangle, Clock,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { ConvertCreditWarning } from '../dealers/ConvertCreditWarning';
import { readCreditWarning, type CreditWarning } from '../dealers/dealerLedgerApi';
import { ProductGridSelector, type ProductGridItem } from '@/components/shared/ProductGridSelector';
import { GstModeMixedModal } from '@/components/sales/GstModeMixedModal';
import { ChargesSection, normalizeCharges, computeChargeTotals, type ProposalCharge } from './ChargesSection';

const SELLER_STATE_CODE = '06';

const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' },
  { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
];

const GST_RATES = [0, 5, 12, 18, 28];

interface LineItem {
  id: string;
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
  discount: number;
  productId: string | null;
  maxDiscount: number | null;
  discountApprovalEnabled: boolean;
  productType?: string;
  serviceCategory?: string | null;
  /** Per-line GST mode. null = follows document mode. */
  gstMode?: 'extra' | 'inclusive' | null;
  /** Origin marker retained from the calculator seed (parity with mobile
   *  calc_seed_lock.dart). meta.seedSource === 'calc-seed' → the line's qty +
   *  description are engine-authoritative and locked in the editor. */
  meta?: { seedSource?: string; optionKey?: string } | null;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  address?: { street?: string; city?: string; state?: string; pincode?: string } | null;
}

interface Account {
  id: string;
  name: string;
}

interface Deal {
  id: string;
  title: string;
}

interface AccountDetail {
  id: string;
  name: string;
  gstin?: string;
  phone?: string;
  email?: string;
  billingAddress?: { street?: string; city?: string; state?: string; pincode?: string } | null;
  tradeName?: string;
  legalName?: string;
}

interface TermOption {
  key: string;
  label: string;
  isDefault?: boolean;
  isEditable?: boolean;
  addsLineItem?: boolean;
}

interface ProposalTerm {
  id: string;
  termName: string;
  termSlug: string;
  sortOrder: number;
  options: TermOption[];
  isEditable: boolean;
  editableHint?: string;
  allowCustom: boolean;
  scope: string;
  conditionType?: string;
  conditionConfig?: any;
}

interface TermTemplate {
  id: string;
  name: string;
  slug: string;
  description?: string;
  scope: string;
  isDefault: boolean;
  selections: { termId: string; termSlug: string; selectedOptionKey: string; customValue?: string }[];
}

interface ProposalSection {
  id: string;
  name: string;
  slug: string;
  type: string;
  contentHtml: string;
  sortOrder: number;
  scope: string;
}

interface SelectedTermValue {
  termName: string;
  termSlug: string;
  selectedOption: string;
  customValue?: string;
}

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

// WS-C — held-state parse, mirrors mobile proposal_gate.dart. The GET
// /proposals/:id response carries the gate token at the envelope level
// (`proposalGateStatus`, normalized 'held'|'approved'|'none'), OR on the
// proposal row, OR as `gate.status`; tolerate one level of `data:` unwrap.
// Held tokens: held / pending / pending_approval (case-insensitive).
function proposalGateToken(d: any): string | null {
  if (!d || typeof d !== 'object') return null;
  const top = d.proposalGateStatus;
  if (typeof top === 'string' && top.trim()) return top;
  const gate = d.gate;
  if (gate && typeof gate === 'object' && typeof gate.status === 'string' && gate.status.trim()) return gate.status;
  if (d.data && typeof d.data === 'object') return proposalGateToken(d.data);
  return null;
}
function isProposalHeld(d: any): boolean {
  const t = proposalGateToken(d)?.trim().toLowerCase();
  return t === 'held' || t === 'pending' || t === 'pending_approval';
}

// ── 🔒 THE ADDITIONAL ALLOWANCE (the document-level "Proposal Discount") ──────
//
// For Sun Stellar this is NOT a discount: it is money they choose not to bill.
// There is NO approval step. At or under the strictest line's own allowance the
// rep just gives it; above it the document is REFUSED and nobody can clear it.
// One line whose product allows none refuses the WHOLE order.
//
// 🔒 This mirrors the server EXACTLY — it is a client-side courtesy so the rep
// finds out BEFORE quoting the customer, not after. It must never disagree with
//   backend/.../discount/additional-allowance-guard.ts
//   backend/.../discount/gate.ts  (resolveAdditionalAllowancePct)
// which take the MIN of maxAdditionalDiscountPct across resolvable lines,
// SKIPPING nulls and excluded lines, and fall back to 7.5 when nothing resolves.
export const DEFAULT_ADDITIONAL_ALLOWANCE_PCT = 7.5;

export interface AllowanceFacts {
  allowsAdditionalDiscount: boolean;
  maxAdditionalDiscountPct: number | null;
}

/** MIN across resolvable lines; nulls + excluded lines skipped; 7.5 fallback. */
export function resolveAdditionalAllowanceCeiling(
  keys: ReadonlyArray<string | null | undefined>,
  facts: Readonly<Record<string, AllowanceFacts>>,
): number {
  let limit: number | null = null;
  for (const k of keys ?? []) {
    const key = (k ?? '').toString().trim();
    if (!key) continue;
    const f = facts[key];
    if (!f || f.allowsAdditionalDiscount === false || f.maxAdditionalDiscountPct == null) continue;
    const v = Number(f.maxAdditionalDiscountPct);
    if (!Number.isFinite(v)) continue;
    limit = limit === null ? v : Math.min(limit, v);
  }
  return limit ?? DEFAULT_ADDITIONAL_ALLOWANCE_PCT;
}

/** 6.000000000000001 → "6"; 7.5 → "7.5". Keeps the refusal sentence readable. */
export const fmtAllowancePct = (n: number): string => String(Number((Number(n) || 0).toFixed(2)));

export function ProposalEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditWarning, setCreditWarning] = useState<
    { warning: CreditWarning; salesOrderId: string | null } | null
  >(null);
  const [discountOverLimitItems, setDiscountOverLimitItems] = useState<LineItem[]>([]);
  const [raisingDiscountRequest, setRaisingDiscountRequest] = useState(false);
  const [discountRequestSuccess, setDiscountRequestSuccess] = useState(false);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);

  // Form state
  const [title, setTitle] = useState('');
  const [proposalStatus, setProposalStatus] = useState('draft');
  // log6 (2026-07-26): per-proposal PDF layout toggle (structured grouped spec
  // blocks vs flat description→value list). Persisted via PATCH
  // /proposals/:id/render-mode; the next "Download PDF" re-renders server-side.
  const [renderMode, setRenderMode] = useState<'structured' | 'flat'>('structured');
  const [renderModeSaving, setRenderModeSaving] = useState(false);
  const [contactId, setContactId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [dealId, setDealId] = useState('');
  // WS-D — lead the editor was opened from (mobile passes ?leadId=). Stamped
  // into both the proposal-create and discount-request payloads so the proposal
  // lands under the lead on mobile.
  const [leadId, setLeadId] = useState('');
  // WS-C — proposal is held pending discount approval (gate-before-generate).
  // Blocks PDF download + send while true.
  const [isHeld, setIsHeld] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [buyerState, setBuyerState] = useState('');
  const [customerType, setCustomerType] = useState('b2c');
  // Calc-sourced proposals own their line rates (engine-priced) — the base-price
  // lock must NEVER re-derive those from the flat catalog. Set on load.
  const [isCalcProposal, setIsCalcProposal] = useState(false);
  const [notes, setNotes] = useState('');

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: null, maxDiscount: null, discountApprovalEnabled: true },
  ]);

  const [showProductGrid, setShowProductGrid] = useState(false);

  // Document-level discount
  const [docDiscountType, setDocDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [docDiscountValue, setDocDiscountValue] = useState<number>(0);
  const [charges, setCharges] = useState<ProposalCharge[]>([]);
  const [maxDocDiscountPct, setMaxDocDiscountPct] = useState<number>(100);
  // 🔒 Per-product additional-allowance facts from /proposals/line-pricing,
  // keyed by productId (and sku). Empty ⇒ the 7.5 fallback, same as the server.
  const [allowanceFacts, setAllowanceFacts] = useState<Record<string, AllowanceFacts>>({});

  // Document GST mode (extra = GST added on top, inclusive = price includes GST)
  const [docGstMode, setDocGstMode] = useState<'extra' | 'inclusive'>('extra');

  // GST mixed-mode modal state
  const [gstMixedModalOpen, setGstMixedModalOpen] = useState(false);
  const [gstMixedInfo, setGstMixedInfo] = useState<{
    inclusiveCount: number;
    extraCount: number;
    recommendation?: 'convert_all_to_doc_mode' | 'switch_doc_mode' | 'split';
  } | null>(null);

  // Inline account creation
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountGstin, setNewAccountGstin] = useState('');
  const [newAccountHasGst, setNewAccountHasGst] = useState<boolean | null>(null);
  const [gstFetching, setGstFetching] = useState(false);
  const [gstFetched, setGstFetched] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Contact detail editing
  const [showContactEdit, setShowContactEdit] = useState(false);
  const [contactCity, setContactCity] = useState('');
  const [contactState, setContactState] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  // Proposal Builder state
  const [termTemplates, setTermTemplates] = useState<TermTemplate[]>([]);
  const [allTerms, setAllTerms] = useState<ProposalTerm[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTermValues, setSelectedTermValues] = useState<SelectedTermValue[]>([]);
  const [showTermsSection, setShowTermsSection] = useState(false);

  // Service prompt state — tracks which service prompts have been shown/answered
  const [servicePromptAnswers, setServicePromptAnswers] = useState<Record<string, string>>({});

  // Inline service product prompts — tracks direct-add service items (keyed by lineItem id)
  // Values: 'pending' | 'inclusive' | 'quoted_separately' | 'not_included'
  const [serviceItemPrompts, setServiceItemPrompts] = useState<Record<string, string>>({});

  const [proposalSections, setProposalSections] = useState<ProposalSection[]>([]);
  const [includedSections, setIncludedSections] = useState<string[]>([]);
  const [showContentSections, setShowContentSections] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [coverLetterOverride, setCoverLetterOverride] = useState('');

  const [accountDetail, setAccountDetail] = useState<AccountDetail | null>(null);
  const [showClientDetails, setShowClientDetails] = useState(true);

  const [detectedScope, setDetectedScope] = useState<string>('all');

  const isIntraState = buyerState === SELLER_STATE_CODE;

  // Auto-fill buyer state from selected contact's address
  const handleContactChange = (newContactId: string) => {
    setContactId(newContactId);
    if (newContactId) {
      const contact = contacts.find(c => c.id === newContactId);
      // Auto-fill customerType from contact
      if ((contact as any)?.customerType) setCustomerType((contact as any).customerType);
      if (contact?.address && typeof contact.address === 'object') {
        const addr = contact.address as { state?: string };
        if (addr.state) {
          const stateMatch = INDIAN_STATES.find(s => s.name.toLowerCase() === addr.state!.toLowerCase() || s.code === addr.state);
          if (stateMatch) setBuyerState(stateMatch.code);
        }
      }
    }
  };

  // Fetch proposal builder data on mount
  useEffect(() => {
    api.get('/proposal-builder/term-templates').then(res => {
      const templates = res.data.data ?? [];
      setTermTemplates(templates);
    }).catch(() => {});
    api.get('/proposal-builder/terms').then(res => {
      setAllTerms(res.data.data ?? []);
    }).catch(() => {});
    api.get('/proposal-builder/sections').then(res => {
      setProposalSections(res.data.data ?? []);
    }).catch(() => {});
    // Fetch discount policy
    api.get('/settings/discount-policy').then(res => {
      const policy = res.data?.data;
      if (policy?.maxDocumentDiscountPct) setMaxDocDiscountPct(policy.maxDocumentDiscountPct);
      if (policy?.defaultDiscountInputMode) setDocDiscountType(policy.defaultDiscountInputMode as any);
    }).catch(() => {});
  }, []);

  // ── Base-price + discount-cap LOCK ──
  // The rate cell is read-only; the authoritative base price (per customer type)
  // and the per-customer-type discount ceiling both come from the server. When a
  // product is added OR the customer type changes, pull the locked values for
  // every product-backed line and apply them. Calc-sourced proposals are skipped
  // (their rates are engine-owned). The backend re-derives on save regardless, so
  // a fetch failure only affects the preview, never the stored price.
  const productLineKey = useMemo(
    () => lineItems.filter(li => li.productId).map(li => li.productId).sort().join(','),
    [lineItems],
  );
  useEffect(() => {
    if (isCalcProposal) return;
    const pids = productLineKey ? productLineKey.split(',') : [];
    if (pids.length === 0) return;
    let cancelled = false;
    api.post('/proposals/line-pricing', { productIds: pids, customerType })
      .then(res => {
        if (cancelled) return;
        const map = (res.data?.data ?? {}) as Record<string, any>;
        // 🔒 The additional-allowance facts ride on the SAME payload.
        const nextFacts: Record<string, AllowanceFacts> = {};
        for (const [k, v] of Object.entries(map)) {
          nextFacts[k] = {
            allowsAdditionalDiscount: (v as any)?.allowsAdditionalDiscount !== false,
            maxAdditionalDiscountPct:
              (v as any)?.maxAdditionalDiscountPct != null ? Number((v as any).maxAdditionalDiscountPct) : null,
          };
        }
        setAllowanceFacts(nextFacts);
        setLineItems(prev => prev.map(li => {
          const r = li.productId ? map[li.productId] : null;
          if (!r) return li;
          const nextRate = typeof r.basePrice === 'number' ? r.basePrice : li.rate;
          // 🔒 2026-08-29 (Manak) — block at the PRODUCT'S OWN CEILING, not at
          // slab 1. The ladder is "rep gives slab 1, boss approves slab 2,
          // above the product ceiling nothing can be approved". Between slab 1
          // and the ceiling the SAVE MUST GO THROUGH: the server holds the
          // document and raises the approval request itself. Blocking at slab 1
          // here made the website stricter than the phone for no stated reason —
          // a rep had to press a separate button to ask for something the phone
          // asks for automatically.
          const nextMax = r.discountApprovalEnabled === false
            ? null
            : (r.productMaxDiscountPct ?? li.maxDiscount);
          const nextAppr = r.discountApprovalEnabled !== false;
          if (li.rate === nextRate && li.maxDiscount === nextMax && li.discountApprovalEnabled === nextAppr) return li;
          return { ...li, rate: nextRate, maxDiscount: nextMax, discountApprovalEnabled: nextAppr };
        }));
      })
      .catch(() => {/* best-effort — server enforces on save */});
    return () => { cancelled = true; };
  }, [customerType, productLineKey, isCalcProposal]);

  // Fetch account detail when account is selected
  useEffect(() => {
    if (!accountId) {
      setAccountDetail(null);
      return;
    }
    api.get(`/accounts/${accountId}`).then(res => {
      const a = res.data.data;
      setAccountDetail({
        id: a.id,
        name: a.name,
        gstin: a.gstin || undefined,
        phone: a.phone || undefined,
        email: a.email || undefined,
        billingAddress: a.billingAddress || null,
        tradeName: a.tradeName || undefined,
        legalName: a.legalName || undefined,
      });
    }).catch(() => setAccountDetail(null));
  }, [accountId]);

  // Scope detection from line items
  useEffect(() => {
    const descriptions = lineItems.map(li => li.description.toLowerCase());
    const hasHeatPump = descriptions.some(d => d.includes('heat pump') || d.includes('heatpump'));
    const hasDomestic = descriptions.some(d =>
      (d.includes('water tank') || d.includes('ss tank')) &&
      (/\b(100|200|300|500|750|1000)\s?(l|ltr|litre)\b/.test(d) || d.includes('domestic'))
    );
    if (hasHeatPump) {
      setDetectedScope('commercial');
    } else if (hasDomestic) {
      setDetectedScope('domestic');
    } else {
      setDetectedScope('all');
    }
  }, [lineItems]);

  // Detect active service prompts — which service_prompt terms apply based on having non-service line items
  const activeServicePrompts = allTerms.filter(term => {
    if (term.conditionType !== 'service_prompt') return false;
    // Show service prompts when there are any non-service product line items
    const hasNonServiceItems = lineItems.some(li => li.description.trim() !== '' && li.productType !== 'Service');
    return hasNonServiceItems;
  });

  // Handle service prompt selection
  const handleServicePromptSelect = (termSlug: string, optionKey: string) => {
    setServicePromptAnswers(prev => ({ ...prev, [termSlug]: optionKey }));
    // Also set in term values so it flows into proposal T&C
    handleTermOverride(termSlug, optionKey);

    // If "quoted_separately" is selected, auto-add a line item for the service product
    const term = allTerms.find(t => t.termSlug === termSlug);
    if (term?.conditionConfig?.serviceProductName && optionKey === 'quoted_separately') {
      const serviceName = term.conditionConfig.serviceProductName;
      // Check if already added
      const alreadyAdded = lineItems.some(li => li.description === serviceName && li.productType === 'Service');
      if (!alreadyAdded) {
        // Fetch and add the service product
        api.get(`/products?search=${encodeURIComponent(serviceName)}&productType=Service&limit=1`).then(res => {
          const products = res.data?.data;
          const p = Array.isArray(products) ? products[0] : null;
          if (p) {
            setLineItems(prev => [...prev, {
              id: generateId(),
              description: p.name,
              hsnSac: p.hsnCode || '',
              quantity: 1,
              rate: p.unitPrice || 0,
              gstRate: p.gstRate || 18,
              discount: 0,
              productId: p.id,
              maxDiscount: p.maxDiscount ?? null,
              discountApprovalEnabled: p.discountApprovalEnabled !== false,
              productType: p.productType || 'Service',
              serviceCategory: term.conditionConfig?.promptGroup || null,
            }]);
          }
        }).catch(() => {
          // Fallback: add a manual service line item
          setLineItems(prev => [...prev, {
            id: generateId(),
            description: serviceName,
            hsnSac: '',
            quantity: 1,
            rate: 0,
            gstRate: 18,
            discount: 0,
            productId: null,
            maxDiscount: null,
            discountApprovalEnabled: false,
            productType: 'Service',
            serviceCategory: term.conditionConfig?.promptGroup || null,
          }]);
        });
      }
    }

    // If switching from "quoted_separately" to another option, remove the auto-added service line item
    if (optionKey !== 'quoted_separately' && term?.conditionConfig?.serviceProductName) {
      const serviceName = term.conditionConfig.serviceProductName;
      setLineItems(prev => prev.filter(li => !(li.description === serviceName && li.productType === 'Service')));
    }
  };

  // Auto-select default template matching detected scope
  useEffect(() => {
    if (selectedTemplateId) return; // user already picked one
    if (termTemplates.length === 0) return;
    if (allTerms.length === 0) return; // wait for terms to load
    // Find default template matching scope, or any default
    const scopeMatch = termTemplates.find(t => t.isDefault && (t.scope === detectedScope || t.scope === 'all'));
    const anyDefault = termTemplates.find(t => t.isDefault);
    const toSelect = scopeMatch || anyDefault;
    if (toSelect) {
      handleTemplateSelect(toSelect.id);
    }
  }, [termTemplates, detectedScope, allTerms]);

  // Apply template selections to term values
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = termTemplates.find(t => t.id === templateId);
    if (!template || !template.selections) return;
    const selections = Array.isArray(template.selections) ? template.selections : [];
    const values: SelectedTermValue[] = allTerms.map(term => {
      const sel = selections.find((s: any) => s.termSlug === term.termSlug || s.termId === term.id);
      const defaultOpt = (term.options as TermOption[])?.find(o => o.isDefault);
      return {
        termName: term.termName,
        termSlug: term.termSlug,
        selectedOption: sel?.selectedOptionKey || defaultOpt?.key || '',
        customValue: sel?.customValue,
      };
    });
    setSelectedTermValues(values);
  }, [termTemplates, allTerms]);

  // Update a single term value override
  const handleTermOverride = (termSlug: string, optionKey: string, customValue?: string) => {
    setSelectedTermValues(prev =>
      prev.map(tv =>
        tv.termSlug === termSlug
          ? { ...tv, selectedOption: optionKey, customValue: optionKey === '__custom__' ? (customValue ?? '') : undefined }
          : tv
      )
    );
  };

  // Toggle a section inclusion
  const toggleSection = (slug: string) => {
    setIncludedSections(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const [inlineGstEnrichment, setInlineGstEnrichment] = useState<any>(null);

  // GST lookup for inline account creation
  const handleInlineGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setGstFetching(true);
    setGstFetched(false);
    setInlineGstEnrichment(null);
    try {
      const res = await api.get(`/accounts/gst-lookup/${gstin}`);
      const d = res.data.data;
      if (d.companyName || d.tradeName || d.legalName) {
        setNewAccountName(d.companyName || d.tradeName || d.legalName);
      }
      if (d.source === 'api') {
        setInlineGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
        });
      }
      setGstFetched(true);
    } catch {
      // Fallback — user can still type name manually
    } finally {
      setGstFetching(false);
    }
  };

  // Create new account inline
  const handleCreateAccount = async () => {
    if (!newAccountName.trim()) return;
    setCreatingAccount(true);
    try {
      const payload: any = { name: newAccountName.trim(), type: 'customer' };
      if (newAccountGstin.length === 15) payload.gstin = newAccountGstin;
      // Include GST enrichment data if available
      if (inlineGstEnrichment) Object.assign(payload, inlineGstEnrichment);
      const res = await api.post('/accounts', payload);
      const newAcc = res.data.data;
      setAccounts(prev => [...prev, { id: newAcc.id, name: newAcc.name }]);
      setAccountId(newAcc.id);
      // Auto-link selected Contact to this new Account (B2B linking)
      if (contactId) {
        try {
          await api.post('/accounts/link-contact', { contactId, accountId: newAcc.id });
        } catch { /* non-critical — Account created, link is best-effort */ }
      }
      setNewAccountName('');
      setNewAccountGstin('');
      setNewAccountHasGst(null);
      setGstFetched(false);
      setShowNewAccount(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  // Save contact detail edits
  const handleSaveContact = async () => {
    if (!contactId) return;
    setSavingContact(true);
    try {
      const updateData: any = {};
      if (contactCity) updateData.city = contactCity;
      if (contactPhone) updateData.phone = contactPhone;
      if (contactEmail) updateData.email = contactEmail;
      if (contactState) {
        updateData.address = { state: contactState };
      }
      await api.patch(`/contacts/${contactId}`, updateData);
      // Also update buyer state if contact state changed
      if (contactState) {
        const stateMatch = INDIAN_STATES.find(s => s.name.toLowerCase() === contactState.toLowerCase() || s.code === contactState);
        if (stateMatch) setBuyerState(stateMatch.code);
      }
      setShowContactEdit(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  // Fetch dropdowns
  useEffect(() => {
    api.get('/contacts').then(res => {
      setContacts(res.data.data?.contacts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/accounts?type=customer&limit=100').then(res => {
      setAccounts(res.data.data?.accounts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/deals').then(res => {
      setDeals(res.data.data?.deals ?? res.data.data ?? []);
    }).catch(() => {});
  }, []);

  // Prefill from URL params (used by AURA Sales mobile app to pre-populate from call context)
  // URL: /proposals/new?contactId=X&accountId=Y&dealId=Z&product=SS+Water+Tank+1000L&contactName=Seema
  useEffect(() => {
    if (!isNew) return;
    const qContactId = searchParams.get('contactId');
    const qAccountId = searchParams.get('accountId');
    const qDealId = searchParams.get('dealId');
    const qLeadId = searchParams.get('leadId');
    const qProduct = searchParams.get('product');
    const qContactName = searchParams.get('contactName');

    if (qContactId) setContactId(qContactId);
    if (qAccountId) setAccountId(qAccountId);
    if (qDealId) setDealId(qDealId);
    if (qLeadId) setLeadId(qLeadId);

    // Auto-generate title from contact name and product
    if (qContactName || qProduct) {
      const parts = ['Proposal'];
      if (qContactName) parts.push(`for ${qContactName}`);
      if (qProduct) parts.push(`— ${qProduct}`);
      setTitle(parts.join(' '));
    }

    // If product specified, try to find and add it as first line item
    if (qProduct) {
      api.get(`/products?search=${encodeURIComponent(qProduct)}&limit=5`).then(res => {
        const products = res.data.data?.products ?? res.data.data ?? [];
        if (products.length > 0) {
          const p = products[0];
          setLineItems([{
            id: generateId(),
            description: p.name || qProduct,
            hsnSac: p.hsnSac || p.hsnCode || '',
            quantity: 1,
            rate: p.sellingPrice || p.price || 0,
            gstRate: p.gstRate ?? 18,
            discount: 0,
            productId: p.id || null,
            maxDiscount: p.maxDiscount ?? null,
            discountApprovalEnabled: p.discountApprovalEnabled !== false,
            productType: p.productType || undefined,
          }]);
        }
      }).catch(() => {});
    }

    // Auto-fill buyer state from contact + load contact details for editing
    if (qContactId) {
      api.get(`/contacts/${qContactId}`).then(res => {
        const c = res.data.data;
        if (c?.address && typeof c.address === 'object') {
          const addr = c.address as { state?: string };
          if (addr.state) {
            const stateMatch = INDIAN_STATES.find(s => s.name.toLowerCase() === addr.state!.toLowerCase() || s.code === addr.state);
            if (stateMatch) setBuyerState(stateMatch.code);
            setContactState(addr.state || '');
          }
        }
        // Pre-populate contact edit fields
        if (c?.city) setContactCity(c.city);
        if (c?.phone) setContactPhone(c.phone);
        if (c?.email) setContactEmail(c.email);
        // Also set account from contact if not provided
        if (!qAccountId && c?.accountId) setAccountId(c.accountId);
        // Auto-link deal from contact's deals if not provided
        if (!qDealId && c?.deals && c.deals.length > 0) {
          setDealId(c.deals[0].id);
        }
      }).catch(() => {});
    }
  }, [isNew, searchParams]);

  // If editing, load existing proposal
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/proposals/${id}`)
      .then(res => {
        const p = res.data.data;
        setTitle(p.title || '');
        setProposalStatus(p.status || 'draft');
        setContactId(p.contactId || '');
        setAccountId(p.accountId || '');
        setDealId(p.dealId || '');
        setLeadId(p.leadId || '');
        // WS-C — held while a discount request gates generation.
        setIsHeld(isProposalHeld(res.data));
        setValidUntil(p.validUntil ? p.validUntil.split('T')[0] : '');
        setNotes(p.notes || '');
        // Load buyerState from content JSON
        const content = typeof p.content === 'object' && p.content ? p.content : {};
        if (content.buyerState) setBuyerState(content.buyerState);
        if (content.termsAndConditions && Array.isArray(content.termsAndConditions)) {
          setSelectedTermValues(content.termsAndConditions);
          setShowTermsSection(true);
        }
        if (content.includedSections && Array.isArray(content.includedSections)) {
          setIncludedSections(content.includedSections);
          if (content.includedSections.length > 0) setShowContentSections(true);
        }
        if (content.coverLetterOverride) setCoverLetterOverride(content.coverLetterOverride);
        if (content.selectedTemplateId) setSelectedTemplateId(content.selectedTemplateId);
        if (content.docDiscountType) setDocDiscountType(content.docDiscountType);
        if (content.docDiscountValue != null) setDocDiscountValue(Number(content.docDiscountValue));
        setCharges(normalizeCharges((content as any).charges));
        if (content.scope) setDetectedScope(content.scope);
        if (p.gstMode) setDocGstMode(p.gstMode as 'extra' | 'inclusive');
        // log6: column is canonical, content.renderMode is the fallback mirror.
        const rm = (p as any).renderMode ?? content.renderMode;
        setRenderMode(rm === 'flat' ? 'flat' : 'structured');
        setIsCalcProposal(!!(p as any).calculatorKey);
        const storedItems = content.lineItems || p.lineItems;
        if (storedItems && storedItems.length > 0) {
          setLineItems(storedItems.map((li: any) => ({
            id: li.id || generateId(),
            description: li.description || '',
            hsnSac: li.hsnSac || '',
            quantity: li.quantity || 1,
            rate: li.rate || 0,
            gstRate: li.gstRate != null ? li.gstRate : 18,
            discount: li.discount || 0,
            productId: li.productId || null,
            maxDiscount: li.maxDiscount ?? null,
            discountApprovalEnabled: li.discountApprovalEnabled !== false,
            productType: li.productType || undefined,
            serviceCategory: li.serviceCategory || null,
            gstMode: li.gstMode ?? null,
            meta: li.meta ?? null,
          })));
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load proposal'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // Calculations with GST split (CGST/SGST or IGST)
  // Document ("additional") discount REDUCES the GST taxable base (applied per-line before GST).
  const calculations = useMemo(() => {
    const round2 = (n: number) => Math.round(n * 100) / 100;

    // Pass 1: line taxables (after per-line discount) → Subtotal (pre-doc-discount).
    const lineTaxables = lineItems.map(item => {
      const grossAmount = item.quantity * item.rate;
      const discountAmount = (grossAmount * (item.discount || 0)) / 100;
      const taxableAmount = round2(grossAmount - discountAmount);
      return { grossAmount, discountAmount, taxableAmount };
    });
    const subtotal = round2(lineTaxables.reduce((s, l) => s + l.taxableAmount, 0));

    // Document-level discount — known before per-line GST so it can reduce the taxable base.
    let docDiscountAmt = 0;
    let docDiscountPct = 0;
    if (docDiscountType === 'percentage') {
      docDiscountPct = Math.min(Math.max(docDiscountValue, 0), 100);
      docDiscountAmt = round2(subtotal * docDiscountPct / 100);
    } else {
      docDiscountAmt = round2(Math.min(docDiscountValue, subtotal));
      docDiscountPct = subtotal > 0 ? (docDiscountValue / subtotal) * 100 : 0;
    }
    const afterDocDiscount = subtotal - docDiscountAmt;
    const docFactor = 1 - docDiscountPct / 100;

    let goodsCgst = 0;
    let goodsSgst = 0;
    let goodsIgst = 0;

    // Pass 2: per-line GST on the doc-discount-REDUCED taxable base.
    const itemCalcs = lineItems.map((item, idx) => {
      const { grossAmount, discountAmount, taxableAmount } = lineTaxables[idx];
      const reducedTaxable = taxableAmount * docFactor;
      const gstAmount = (reducedTaxable * item.gstRate) / 100;
      let cgst = 0;
      let sgst = 0;
      let igst = 0;

      if (buyerState) {
        if (isIntraState) {
          cgst = gstAmount / 2;
          sgst = gstAmount / 2;
        } else {
          igst = gstAmount;
        }
      }

      goodsCgst += cgst;
      goodsSgst += sgst;
      goodsIgst += igst;

      return { grossAmount, discountAmount, taxableAmount, gstAmount, cgst, sgst, igst, total: taxableAmount + gstAmount };
    });

    // Charges sit OUTSIDE the discount base — added after the doc discount,
    // taxed at their own rate, never reduced by any discount.
    const ct = computeChargeTotals(charges, isIntraState ? 'intra_state' : 'inter_state');

    // ── ONE tax-breakdown convention (2026-08-20, final review I1/I2) ────────
    // `subtotal` stays GOODS ONLY (it is the document-discount base, so a
    // charge must never enter it). `totalTax` and the cgst/sgst/igst breakdown
    // carry goods tax + charge tax, always — the summary must not show a
    // goods-only tax line beside a charges-inclusive grand total.
    // `chargesSubtotal` keeps its own field and its own displayed line.
    // Identical on all four surfaces (this file,
    // confirm-sales-order-from-proposal.ts, proposal_totals.dart,
    // core/pdf/templates/proposal.ts).
    // No buyer state resolved yet → the intra/inter split is unknown and the
    // per-line cgst/sgst/igst stay 0, so fall back to a flat goods-GST estimate.
    const goodsTax = round2(goodsCgst + goodsSgst + goodsIgst) || round2(
      lineItems.reduce((s, i) => {
        const gross = i.quantity * i.rate;
        const disc = (gross * (i.discount || 0)) / 100;
        return s + ((gross - disc) * docFactor * i.gstRate / 100);
      }, 0),
    );
    const totalTax = round2(goodsTax + ct.chargesTax);
    // Kept as a distinct field only because the summary and older callers read
    // it; under the one convention it is the same figure as `totalTax`.
    const totalGst = totalTax;

    // Numerically identical to the previous goods-tax + charge-tax form.
    const grandTotalRaw = afterDocDiscount + totalTax + ct.chargesSubtotal;
    const roundOff = Math.round(grandTotalRaw) - grandTotalRaw;
    const grandTotal = Math.round(grandTotalRaw);

    return {
      subtotal,
      totalCgst: round2(goodsCgst + ct.chargesCgst),
      totalSgst: round2(goodsSgst + ct.chargesSgst),
      totalIgst: round2(goodsIgst + ct.chargesIgst),
      totalTax,
      totalGst,
      roundOff,
      grandTotal,
      itemCalcs,
      docDiscountAmt,
      docDiscountPct,
      afterDocDiscount,
      chargesSubtotal: ct.chargesSubtotal,
      chargesTax: ct.chargesTax,
      chargesCgst: ct.chargesCgst,
      chargesSgst: ct.chargesSgst,
      chargesIgst: ct.chargesIgst,
    };
  }, [lineItems, buyerState, isIntraState, docDiscountType, docDiscountValue, charges]);

  // 🔒 The order's additional-allowance ceiling — MIN across the lines that
  // resolve, nulls skipped, 7.5 when nothing resolves. A MIXED order takes the
  // STRICTEST line.
  const namedLines = useMemo(() => lineItems.filter(li => li.description.trim() !== ''), [lineItems]);
  const additionalAllowanceLimitPct = useMemo(
    () => resolveAdditionalAllowanceCeiling(namedLines.map(li => li.productId), allowanceFacts),
    [namedLines, allowanceFacts],
  );
  // One line whose product allows NONE refuses the whole order.
  const allowanceExcludedLines = useMemo(
    () => namedLines.filter(li => li.productId && allowanceFacts[li.productId]?.allowsAdditionalDiscount === false),
    [namedLines, allowanceFacts],
  );

  const addLineItem = () => {
    setLineItems([...lineItems, { id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: null, maxDiscount: null, discountApprovalEnabled: true }]);
  };

  const removeLineItem = (itemId: string) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter(li => li.id !== itemId));
  };

  const updateLineItem = (itemId: string, field: keyof LineItem, value: string | number) => {
    setLineItems(lineItems.map(li => li.id === itemId ? { ...li, [field]: value } : li));
  };

  // WS-B — a line is "calc-seeded" when it carries the calculator's per-line
  // seed marker (parity with mobile calc_seed_lock.dart:
  // meta.seedSource === 'calc-seed'). Fallback: an engine-priced line (has a
  // productId) inside a calc-sourced proposal whose stored rows predate the
  // per-line marker. A manually-added blank row (no product, no marker) in a
  // calc proposal stays fully editable. Calc-seeded lines lock qty +
  // description (re-run the calculator to change); rate is read-only for ALL
  // lines regardless (WS-B strict).
  const isCalcSeededLine = useCallback((line: LineItem): boolean => {
    if (line.meta?.seedSource === 'calc-seed') return true;
    return isCalcProposal && !!line.productId;
  }, [isCalcProposal]);

  const handleProductSelect = (product: ProductGridItem) => {
    const newId = generateId();
    const pType = (product as any).productType || undefined;
    setLineItems(prev => [
      ...prev,
      {
        id: newId,
        description: product.name,
        hsnSac: product.hsnCode || '',
        quantity: 1,
        rate: product.unitPrice,
        gstRate: product.gstRate,
        discount: 0,
        productId: product.id,
        maxDiscount: product.maxDiscount ?? null,
        discountApprovalEnabled: (product as any).discountApprovalEnabled !== false,
        productType: pType,
      },
    ]);
    // Auto-prompt for service products that are not already handled by proposal builder service_prompt terms
    if (pType === 'Service') {
      const handledByTerms = activeServicePrompts.some(t =>
        t.conditionConfig?.serviceProductName === product.name
      );
      if (!handledByTerms) {
        setServiceItemPrompts(prev => ({ ...prev, [newId]: 'pending' }));
      }
    }
  };

  // Handle inline service item prompt selection
  const handleServiceItemPrompt = (lineItemId: string, choice: 'inclusive' | 'quoted_separately' | 'not_included') => {
    const item = lineItems.find(li => li.id === lineItemId);
    if (!item) return;
    const serviceName = item.description || 'Service';

    setServiceItemPrompts(prev => ({ ...prev, [lineItemId]: choice }));

    if (choice === 'inclusive') {
      // Set rate to 0, add T&C note
      setLineItems(prev => prev.map(li =>
        li.id === lineItemId ? { ...li, rate: 0 } : li
      ));
      const tcNote = `${serviceName}: Inclusive`;
      setNotes(prev => {
        if (prev.includes(tcNote)) return prev;
        return prev ? `${prev}\n${tcNote}` : tcNote;
      });
    } else if (choice === 'not_included') {
      // Remove the line item, add T&C note
      setLineItems(prev => {
        const filtered = prev.filter(li => li.id !== lineItemId);
        return filtered.length > 0 ? filtered : [{ id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: null, maxDiscount: null, discountApprovalEnabled: true }];
      });
      const tcNote = `${serviceName}: Not Included`;
      setNotes(prev => {
        if (prev.includes(tcNote)) return prev;
        return prev ? `${prev}\n${tcNote}` : tcNote;
      });
      // Clean up prompt state
      setServiceItemPrompts(prev => {
        const next = { ...prev };
        delete next[lineItemId];
        return next;
      });
    }
    // 'quoted_separately' — keep line item as-is with editable price, no changes needed
  };

  const handleRaiseDiscountRequest = async () => {
    if (discountOverLimitItems.length === 0) return;
    try {
      setRaisingDiscountRequest(true);
      setError(null);
      for (const li of discountOverLimitItems) {
        await api.post('/discount-requests', {
          proposalId: isNew ? undefined : id,
          dealId: dealId || undefined,
          leadId: leadId || undefined,
          accountId: accountId || undefined,
          contactId: contactId || undefined,
          productId: li.productId || undefined,
          itemDescription: li.description,
          itemRate: li.rate,
          itemQuantity: li.quantity,
          requestedPct: li.discount,
          reason: `Discount of ${li.discount}% requested on "${li.description}" (max allowed: ${li.maxDiscount}%)`,
        });
      }
      setDiscountRequestSuccess(true);
      setDiscountOverLimitItems([]);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to raise discount request');
    } finally {
      setRaisingDiscountRequest(false);
    }
  };

  // log6: flip the layout mode + persist. Optimistic; reverts on failure.
  // 2026-09-28 (spec A6) — once the quote is sent the customer has the PDF:
  // the format is locked. The server refuses too (409 RENDER_MODE_LOCKED).
  const renderModeLocked = !isNew && proposalStatus !== 'draft';
  const RENDER_MODE_LOCKED_TIP = 'Quote already sent — PDF format is locked';
  const handleRenderModeChange = async (mode: 'structured' | 'flat') => {
    if (isNew || renderModeLocked || mode === renderMode || renderModeSaving) return;
    const prev = renderMode;
    setRenderMode(mode);
    setRenderModeSaving(true);
    try {
      await api.patch(`/proposals/${id}/render-mode`, { renderMode: mode });
    } catch (err: any) {
      setRenderMode(prev);
      alert(err.response?.data?.error || 'Failed to change layout');
    } finally {
      setRenderModeSaving(false);
    }
  };

  const handleSave = async (sendAfterSave = false) => {
    try {
      setSaving(true);
      setError(null);

      if (!title.trim()) {
        setError('Proposal title is required');
        setSaving(false);
        return;
      }

      // Validate document discount limit
      const effDocDiscountPct = calculations.docDiscountPct ?? 0;
      if (effDocDiscountPct > maxDocDiscountPct) {
        setError(`Document discount of ${effDocDiscountPct.toFixed(1)}% exceeds max allowed ${maxDocDiscountPct}%. Please reduce or request approval.`);
        setSaving(false);
        return;
      }

      // ── 🔒 THE ADDITIONAL ALLOWANCE ─────────────────────────────────────
      // Not a discount, and there is NO approval step: at or under the order's
      // ceiling the rep just gives it, above it the document is REFUSED and
      // nobody can clear it. The server 422s on save (additional-allowance-
      // guard.ts); this only tells the rep BEFORE they quote the customer.
      // 🔒 effDocDiscountPct is measured off the NET-of-line-discount subtotal
      // (calculations.subtotal is post-line-discount) — a gross-based percent
      // would put the wall near 14% instead of 7.5% on SS's 40-45% lines.
      if (effDocDiscountPct > 0) {
        if (allowanceExcludedLines.length > 0) {
          const names = allowanceExcludedLines.map(li => li.description.trim()).join(', ');
          setError(`Additional allowance of ${fmtAllowancePct(effDocDiscountPct)}% cannot apply: this order contains line(s) that allow none [${names}]. Refused.`);
          setSaving(false);
          return;
        }
        if (effDocDiscountPct > additionalAllowanceLimitPct) {
          setError(`Additional allowance of ${fmtAllowancePct(effDocDiscountPct)}% exceeds the ${fmtAllowancePct(additionalAllowanceLimitPct)}% limit for this order — no approver can clear this.`);
          setSaving(false);
          return;
        }
      }

      // Validate discount limits (skip items with discount approval disabled)
      const overLimitItems = lineItems.filter(li =>
        li.description.trim() !== '' &&
        li.discount > 0 &&
        li.maxDiscount !== null &&
        li.discount > li.maxDiscount &&
        li.discountApprovalEnabled !== false
      );
      if (overLimitItems.length > 0) {
        const names = overLimitItems.map(li => `"${li.description}" (${li.discount}% > max ${li.maxDiscount}%)`).join(', ');
        setDiscountOverLimitItems(overLimitItems);
        setDiscountRequestSuccess(false);
        // Above the ceiling there is nothing to approve — the server deletes
        // the document and 422s. Offering "raise a request" here promised the
        // rep something nobody could deliver.
        setError(`Discount is above the maximum this product can take: ${names}. No approver can clear this — lower the discount.`);
        setSaving(false);
        return;
      }
      setDiscountOverLimitItems([]);

      // Filter out empty line items to keep stored data clean
      const validItems = lineItems.filter(li => li.description.trim() !== '');

      const buildLineItemPayload = (li: LineItem, forceMode?: 'extra' | 'inclusive') => ({
        description: li.description.trim(),
        hsnSac: li.hsnSac,
        quantity: li.quantity,
        rate: li.rate,
        gstRate: li.gstRate,
        discount: li.discount || 0,
        productId: li.productId || undefined,
        maxDiscount: li.maxDiscount ?? undefined,
        discountApprovalEnabled: li.discountApprovalEnabled !== false ? undefined : false,
        productType: li.productType || undefined,
        serviceCategory: li.serviceCategory || undefined,
        gstMode: forceMode ?? li.gstMode ?? null,
        // WS-B — round-trip the calculator seed marker so the qty/desc lock
        // survives a web edit + reload (backend reconciler stays authoritative).
        meta: li.meta ?? undefined,
      });

      const payload: any = {
        title: title.trim(),
        contactId: contactId || undefined,
        accountId: accountId || undefined,
        dealId: dealId || undefined,
        leadId: leadId || undefined,
        validUntil: validUntil || undefined,
        total: calculations.grandTotal,
        status: sendAfterSave ? 'sent' : 'draft',
        gstMode: docGstMode,
        content: {
          buyerState: buyerState || undefined,
          notes: notes || undefined,
          lineItems: validItems.map(li => buildLineItemPayload(li)),
          docDiscountType: docDiscountType,
          docDiscountValue: docDiscountValue,
          docDiscountPct: calculations.docDiscountPct,
          docDiscountAmt: calculations.docDiscountAmt,
          ...(charges.length ? { charges } : {}),
          termsAndConditions: selectedTermValues.length > 0 ? selectedTermValues : undefined,
          includedSections: includedSections.length > 0 ? includedSections : undefined,
          coverLetterOverride: coverLetterOverride || undefined,
          scope: detectedScope !== 'all' ? detectedScope : undefined,
          selectedTemplateId: selectedTemplateId || undefined,
          gstMode: docGstMode,
        },
      };

      let savedId: string | null = isNew ? null : (id as string);

      if (isNew) {
        const res = await api.post('/proposals', payload);
        savedId = res.data.data?.id ?? null;
        if (!savedId) {
          navigate('/proposals', { replace: true });
          return;
        }
        navigate(`/proposals/${savedId}/edit`, { replace: true });
        if (!sendAfterSave) return;
      } else {
        await api.put(`/proposals/${id}`, payload);
        if (!sendAfterSave) {
          navigate('/proposals');
          return;
        }
      }

      if (sendAfterSave && savedId) {
        // Use the /send endpoint which performs GST mode validation.
        try {
          await api.post(`/proposals/${savedId}/send`);
          navigate('/proposals');
        } catch (sendErr: any) {
          if (sendErr?.response?.status === 422 && sendErr?.response?.data?.error === 'mixed_gst_mode') {
            const d = sendErr.response.data;
            setGstMixedInfo({
              inclusiveCount: d.inclusiveCount ?? 0,
              extraCount: d.extraCount ?? 0,
              recommendation: d.recommendation,
            });
            setGstMixedModalOpen(true);
            // Stay on the editor page so the user can resolve
          } else {
            setError(sendErr?.response?.data?.error || 'Failed to send proposal');
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };

  /** Called when the user picks "Convert all lines to doc mode" in the GST modal. */
  const handleGstConvertAll = async () => {
    setGstMixedModalOpen(false);
    const proposalId = id as string;
    if (!proposalId) return;
    try {
      // Re-save with all line gstModes coerced to docGstMode, then re-send with forceMode.
      await api.post(`/proposals/${proposalId}/send?forceMode=docMode`);
      navigate('/proposals');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to send after mode conversion');
    }
  };

  /** Called when user picks "Switch document mode" in the GST modal. */
  const handleGstSwitchDocMode = () => {
    setDocGstMode(prev => prev === 'extra' ? 'inclusive' : 'extra');
    setGstMixedModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading proposal...</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {creditWarning && (
        <ConvertCreditWarning
          warning={creditWarning.warning}
          onOpenOrder={() =>
            navigate(creditWarning.salesOrderId ? `/sales-orders/${creditWarning.salesOrderId}` : '/sales-orders')
          }
          onDismiss={() => setCreditWarning(null)}
        />
      )}
      {/* Back button */}
      <button
        onClick={() => navigate('/proposals')}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={16} />
        Back to Proposals
      </button>

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? 'Create Proposal' : 'Edit Proposal'}
        </h1>
        <div className="flex items-center gap-2">
          {/* log6 — PDF layout toggle. Persists renderMode; next Download PDF
              re-renders server-side (structured grouped specs vs flat list). */}
          {!isNew && !isHeld && (
            <div
              className={`inline-flex items-center rounded-lg border border-gray-300 overflow-hidden ${renderModeLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              role="group"
              aria-label="PDF pricing detail"
              title={renderModeLocked ? RENDER_MODE_LOCKED_TIP : undefined}
            >
              <span className="px-2 text-[11px] font-medium text-gray-400 select-none">Pricing</span>
              <button
                type="button"
                onClick={() => handleRenderModeChange('structured')}
                disabled={renderModeSaving || renderModeLocked}
                aria-pressed={renderMode === 'structured'}
                title={renderModeLocked ? RENDER_MODE_LOCKED_TIP : 'PDF shows list rate, DISC% and the discount line'}
                className={`px-3 py-2 text-xs font-medium ${renderMode === 'structured' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Show discount
              </button>
              <button
                type="button"
                onClick={() => handleRenderModeChange('flat')}
                disabled={renderModeSaving || renderModeLocked}
                aria-pressed={renderMode === 'flat'}
                title={renderModeLocked ? RENDER_MODE_LOCKED_TIP : 'PDF shows the net rate only — no list rate, no DISC% column, no discount line'}
                className={`px-3 py-2 text-xs font-medium ${renderMode === 'flat' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Net rate only
              </button>
            </div>
          )}
          {/* WS-C — no PDF while held (nothing generated until approved). */}
          {!isNew && !isHeld && (
            <button
              onClick={() => window.open(`/api/v1/proposals/${id}/pdf`, '_blank')}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileDown size={16} />
              Download PDF
            </button>
          )}
          {!isNew && (
            <button
              onClick={async () => {
                if (!confirm('Convert this quotation to a Sales Order? This will lock the line items and start the fulfilment flow.')) return;
                try {
                  const res = await api.post(`/proposals/${id}/convert-to-sales-order`);
                  const soId = res.data?.data?.id ?? res.data?.id;
                  // 🔒 Dealer credit ledger L6 — warn, never block. The order
                  // already exists; the banner shows it and offers the order.
                  const warning = readCreditWarning(res.data);
                  if (warning) {
                    setCreditWarning({ warning, salesOrderId: soId ?? null });
                    return;
                  }
                  if (soId) {
                    navigate(`/sales-orders/${soId}`);
                  } else {
                    alert('Sales order created. Check the Sales Orders list.');
                    navigate('/sales-orders');
                  }
                } catch (err: any) {
                  alert(err.response?.data?.error || 'Failed to convert to Sales Order');
                }
              }}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <ClipboardList size={16} />
              Convert to Sales Order
            </button>
          )}
          {!isNew && proposalStatus === 'accepted' && (
            <button
              onClick={() => navigate(`/invoices/new?fromProposal=${id}`)}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <Receipt size={16} />
              Convert to Invoice
            </button>
          )}
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={16} />}
            Save as Draft
          </button>
          {/* WS-C — "Mark as sent" is blocked while held (would represent a
              held proposal as sent). Save as Draft stays available. */}
          <button
            onClick={() => handleSave(true)}
            disabled={saving || isHeld}
            title={isHeld ? 'Awaiting discount approval — can’t send until approved' : undefined}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={16} />}
            Send Proposal
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p>{error}</p>
              {discountOverLimitItems.length > 0 && (
                <button
                  onClick={handleRaiseDiscountRequest}
                  disabled={raisingDiscountRequest}
                  className="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {raisingDiscountRequest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {raisingDiscountRequest ? 'Raising Request...' : `Raise Discount Request (${discountOverLimitItems.length} item${discountOverLimitItems.length > 1 ? 's' : ''})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {discountRequestSuccess && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <p className="font-medium">Discount request raised successfully!</p>
          <p className="mt-1">Your request has been sent to the admin/sales head for approval. You'll be notified once it's approved. You can save the proposal as draft in the meantime.</p>
        </div>
      )}

      {/* WS-C — held banner: proposal is awaiting discount approval before it
          can be generated/forwarded. Mirrors mobile "Awaiting approval to
          generate proposal". PDF download + Send are disabled while held. */}
      {isHeld && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Clock size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Awaiting approval to generate proposal</p>
            <p className="mt-0.5 text-amber-700">A discount request on this proposal is pending approval. You can keep editing and Save as Draft, but the PDF can't be downloaded or sent until it's approved.</p>
          </div>
        </div>
      )}

      {/* GST Mode Toggle */}
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-[var(--line,#e5e7eb)] bg-white px-4 py-3">
        <span className="text-sm font-medium text-[var(--fg-2,#374151)]">Document GST Mode:</span>
        <div className="flex overflow-hidden rounded-lg border border-[var(--line,#e5e7eb)]">
          <button
            type="button"
            onClick={() => setDocGstMode('extra')}
            className={`px-4 py-1.5 text-xs font-medium transition-colors ${
              docGstMode === 'extra'
                ? 'bg-[var(--saffron,#f59e0b)] text-white'
                : 'bg-white text-[var(--fg-2,#374151)] hover:bg-[var(--ivory-2,#f3f4f6)]'
            }`}
          >
            Extra (+ GST)
          </button>
          <button
            type="button"
            onClick={() => setDocGstMode('inclusive')}
            className={`px-4 py-1.5 text-xs font-medium transition-colors ${
              docGstMode === 'inclusive'
                ? 'bg-[var(--saffron,#f59e0b)] text-white'
                : 'bg-white text-[var(--fg-2,#374151)] hover:bg-[var(--ivory-2,#f3f4f6)]'
            }`}
          >
            Inclusive (incl. GST)
          </button>
        </div>
        <span className="text-xs text-[var(--fg-3,#6b7280)]">
          {docGstMode === 'extra' ? 'GST will be added on top of each line item price' : 'Price already includes GST — tax will be extracted'}
        </span>
      </div>

      {/* Proposal Details */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Proposal Details</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. CRM Implementation Proposal for Acme Corp"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Contact</label>
            <select
              value={contactId}
              onChange={(e) => handleContactChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="">Select Contact</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{joinPersonName(c.firstName, c.lastName)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
            <div className="flex gap-2">
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              >
                <option value="">Select Account</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewAccount(!showNewAccount)}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                title="Create new account"
              >
                <Building2 size={14} />
                <Plus size={12} />
              </button>
            </div>
            {showNewAccount && (
              <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                {newAccountHasGst === null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Has GSTIN?</span>
                    <button type="button" onClick={() => setNewAccountHasGst(true)}
                      className="rounded border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 hover:bg-green-100">
                      Yes
                    </button>
                    <button type="button" onClick={() => setNewAccountHasGst(false)}
                      className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100">
                      No
                    </button>
                  </div>
                )}
                {newAccountHasGst === true && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAccountGstin}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
                        setNewAccountGstin(v);
                        setGstFetched(false);
                        if (v.length === 15) handleInlineGstLookup(v);
                      }}
                      placeholder="Enter GSTIN (15 chars)"
                      maxLength={15}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-mono focus:border-[var(--ink-0)] focus:outline-none"
                    />
                    {gstFetching && <Loader2 size={14} className="animate-spin text-[var(--saffron-600)] mt-1" />}
                  </div>
                )}
                {(newAccountHasGst === false || (newAccountHasGst === true && (gstFetched || newAccountGstin.length === 15))) && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      placeholder={gstFetched ? 'Company name (auto-filled)' : 'New account name'}
                      className={`flex-1 rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none ${gstFetched && newAccountName ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateAccount()}
                    />
                    <button
                      type="button"
                      onClick={handleCreateAccount}
                      disabled={creatingAccount || !newAccountName.trim()}
                      className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {creatingAccount ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                )}
                {newAccountHasGst !== null && (
                  <button type="button" onClick={() => { setNewAccountHasGst(null); setNewAccountGstin(''); setNewAccountName(''); setGstFetched(false); }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline">
                    Back
                  </button>
                )}
              </div>
            )}
          </div>
          {/* Deal is auto-linked — shown read-only if present */}
          {dealId && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Linked Deal</label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                <span>{deals.find(d => d.id === dealId)?.title || 'Deal linked'}</span>
                <span className="text-xs text-gray-400">(auto-linked)</span>
              </div>
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Valid Until</label>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Buyer State (Place of Supply)</label>
            <select
              value={buyerState}
              onChange={(e) => setBuyerState(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="">Select State</option>
              {INDIAN_STATES.map(s => (
                <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Customer Type</label>
            <select
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="b2c">B2C — Individual</option>
              <option value="b2b">B2B — Business</option>
            </select>
          </div>
        </div>
        {buyerState && (
          <p className="mt-3 text-xs text-gray-500">
            {isIntraState
              ? 'Intra-state supply (same state as seller: Haryana) — CGST + SGST applies'
              : 'Inter-state supply — IGST applies'}
          </p>
        )}

        {/* Contact detail editing — collapsible section */}
        {contactId && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => setShowContactEdit(!showContactEdit)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <UserCircle size={16} />
              Update Contact Details
              {showContactEdit ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showContactEdit && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">City</label>
                  <input
                    type="text"
                    value={contactCity}
                    onChange={(e) => setContactCity(e.target.value)}
                    placeholder="City"
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">State</label>
                  <input
                    type="text"
                    value={contactState}
                    onChange={(e) => setContactState(e.target.value)}
                    placeholder="State"
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Phone</label>
                  <input
                    type="text"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="Phone"
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Email</label>
                  <input
                    type="text"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveContact}
                    disabled={savingContact}
                    className="rounded-lg bg-gray-800 px-4 py-1.5 text-xs font-medium text-white hover:bg-gray-900 disabled:opacity-50"
                  >
                    {savingContact ? 'Saving...' : 'Save Contact Changes'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Client Details (auto-filled from selected account) */}
      {accountDetail && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
          <button
            type="button"
            onClick={() => setShowClientDetails(!showClientDetails)}
            className="flex w-full items-center justify-between"
          >
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Client Details</h2>
            {showClientDetails ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {showClientDetails && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <span className="block text-xs font-medium text-gray-500 uppercase">Company</span>
                <span className="text-sm font-medium text-gray-900">{accountDetail.name}</span>
                {accountDetail.tradeName && accountDetail.tradeName !== accountDetail.name && (
                  <span className="block text-xs text-gray-500">Trade: {accountDetail.tradeName}</span>
                )}
              </div>
              {accountDetail.gstin && (
                <div>
                  <span className="block text-xs font-medium text-gray-500 uppercase">GSTIN</span>
                  <span className="text-sm font-mono text-gray-900">{accountDetail.gstin}</span>
                </div>
              )}
              {accountDetail.billingAddress && typeof accountDetail.billingAddress === 'object' && (
                <div>
                  <span className="block text-xs font-medium text-gray-500 uppercase">Billing Address</span>
                  <span className="text-sm text-gray-900">
                    {[
                      (accountDetail.billingAddress as any).street,
                      (accountDetail.billingAddress as any).city,
                      (accountDetail.billingAddress as any).state,
                      (accountDetail.billingAddress as any).pincode,
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {accountDetail.phone && (
                <div>
                  <span className="block text-xs font-medium text-gray-500 uppercase">Phone</span>
                  <span className="text-sm text-gray-900">{accountDetail.phone}</span>
                </div>
              )}
              {accountDetail.email && (
                <div>
                  <span className="block text-xs font-medium text-gray-500 uppercase">Email</span>
                  <span className="text-sm text-gray-900">{accountDetail.email}</span>
                </div>
              )}
              {contactId && contacts.find(c => c.id === contactId) && (
                <div>
                  <span className="block text-xs font-medium text-gray-500 uppercase">Contact Person</span>
                  <span className="text-sm text-gray-900">
                    {joinPersonName(
                      contacts.find(c => c.id === contactId)?.firstName,
                      contacts.find(c => c.id === contactId)?.lastName,
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Proposal Content Sections */}
      {proposalSections.length > 0 && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
          <button
            type="button"
            onClick={() => setShowContentSections(!showContentSections)}
            className="flex w-full items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-[var(--saffron-600)]" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Proposal Content</h2>
              {includedSections.length > 0 && (
                <span className="rounded-full bg-[var(--saffron-50)] px-2 py-0.5 text-xs font-medium text-[var(--ink-0)]">
                  {includedSections.length} included
                </span>
              )}
            </div>
            {showContentSections ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {showContentSections && (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-gray-500">Toggle sections to include in the proposal. Click to preview content.</p>
              {proposalSections
                .filter(s => s.scope === 'all' || s.scope === detectedScope)
                .map(section => {
                  const isIncluded = includedSections.includes(section.slug);
                  const isExpanded = expandedSectionId === section.id;
                  const isCoverLetter = section.type === 'cover_letter';
                  return (
                    <div key={section.id} className={`rounded-lg border ${isIncluded ? 'border-[var(--ink-3)] bg-[var(--saffron-50)]/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => toggleSection(section.slug)}
                            className={`flex h-5 w-5 items-center justify-center rounded border ${isIncluded ? 'border-[var(--ink-0)] bg-[var(--ink-0)] text-white' : 'border-gray-300'}`}
                          >
                            {isIncluded && <CheckSquare size={14} />}
                          </button>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{section.name}</span>
                            <span className="ml-2 text-xs text-gray-400">{section.type}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedSectionId(isExpanded ? null : section.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-gray-100 px-4 py-3">
                          {isCoverLetter && isIncluded ? (
                            <textarea
                              value={coverLetterOverride || section.contentHtml}
                              onChange={(e) => setCoverLetterOverride(e.target.value)}
                              rows={6}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                              placeholder="Customize cover letter content..."
                            />
                          ) : (
                            <div
                              className="prose prose-sm max-w-none text-gray-700"
                              dangerouslySetInnerHTML={{ __html: section.contentHtml || '<em>No content</em>' }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Line Items */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Line Items</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowProductGrid(true)}
              className="flex items-center gap-1 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-3 py-1.5 text-sm font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)]"
            >
              <LayoutGrid size={14} />
              Browse Products
            </button>
            <button
              onClick={addLineItem}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus size={14} />
              Add Item
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-2 py-2 w-[24%]">Description</th>
                <th className="px-2 py-2 w-[8%]">HSN/SAC</th>
                <th className="px-2 py-2 w-[6%] text-right">Qty</th>
                <th className="px-2 py-2 w-[10%] text-right">Rate (INR)</th>
                <th className="px-2 py-2 w-[8%] text-center">Disc %</th>
                <th className="px-2 py-2 w-[8%] text-center">GST %</th>
                {buyerState && isIntraState && (
                  <>
                    <th className="px-2 py-2 w-[10%] text-right">CGST</th>
                    <th className="px-2 py-2 w-[10%] text-right">SGST</th>
                  </>
                )}
                {buyerState && !isIntraState && (
                  <th className="px-2 py-2 w-[10%] text-right">IGST</th>
                )}
                {!buyerState && (
                  <th className="px-2 py-2 w-[10%] text-right">GST Amt</th>
                )}
                <th className="px-2 py-2 w-[12%] text-right">Total</th>
                <th className="px-2 py-2 w-[4%]"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => {
                const calc = calculations.itemCalcs[idx];
                // WS-B — calc-seeded lines lock qty + description (re-run the
                // calculator to change). Manual/ad-hoc lines stay editable.
                const seeded = isCalcSeededLine(item);
                return (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Item description"
                        readOnly={seeded}
                        tabIndex={seeded ? -1 : undefined}
                        title={seeded ? 'Set by the calculator — re-run to change' : undefined}
                        className={`w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none ${
                          seeded
                            ? 'cursor-not-allowed bg-gray-50 text-gray-600'
                            : 'focus:border-[var(--ink-0)]'
                        }`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={item.hsnSac}
                        onChange={(e) => updateLineItem(item.id, 'hsnSac', e.target.value)}
                        placeholder="HSN"
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      {/* WS-B — qty locked on calc-seeded lines: the sizing +
                          ROI pages are frozen to the recommended config, so a
                          diverging qty would contradict the PDF. Re-run the
                          calculator to change. */}
                      <input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        readOnly={seeded}
                        tabIndex={seeded ? -1 : undefined}
                        title={seeded ? 'Set by the calculator — re-run to change' : undefined}
                        className={`w-full rounded border border-gray-200 px-2 py-1.5 text-right text-sm focus:outline-none ${
                          seeded
                            ? 'cursor-not-allowed bg-gray-50 text-gray-600'
                            : 'focus:border-[var(--ink-0)]'
                        }`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      {/* Base price is LOCKED — set from the catalog by customer
                          type (server-authoritative). A rep can't edit it; only
                          quantity + a capped discount move the line total. */}
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.rate}
                        readOnly
                        tabIndex={-1}
                        title="Price is set from the catalog and can't be edited. Apply a discount (within the cap) to lower the line total."
                        className="w-full cursor-not-allowed rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-right text-sm text-gray-600 focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.5"
                          value={item.discount || ''}
                          onChange={(e) => updateLineItem(item.id, 'discount', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className={`w-full rounded border px-2 py-1.5 text-center text-sm focus:outline-none ${
                            item.discount > 0 && item.maxDiscount !== null && item.discount > item.maxDiscount && item.discountApprovalEnabled !== false
                              ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500'
                              : 'border-gray-200 focus:border-[var(--ink-0)]'
                          }`}
                          title={item.discountApprovalEnabled === false ? 'No approval required' : (item.maxDiscount !== null ? `Max allowed: ${item.maxDiscount}%` : 'No limit set')}
                        />
                        {item.maxDiscount !== null && item.discountApprovalEnabled !== false && (
                          <span className={`absolute -bottom-3.5 left-0 text-[10px] ${
                            item.discount > item.maxDiscount ? 'text-red-500 font-medium' : 'text-gray-400'
                          }`}>
                            max {item.maxDiscount}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={item.gstRate}
                        onChange={(e) => updateLineItem(item.id, 'gstRate', parseInt(e.target.value))}
                        className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-center text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      >
                        {GST_RATES.map(r => (
                          <option key={r} value={r}>{r}%</option>
                        ))}
                      </select>
                    </td>
                    {buyerState && isIntraState && (
                      <>
                        <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.cgst ?? 0)}</td>
                        <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.sgst ?? 0)}</td>
                      </>
                    )}
                    {buyerState && !isIntraState && (
                      <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.igst ?? 0)}</td>
                    )}
                    {!buyerState && (
                      <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.gstAmount ?? 0)}</td>
                    )}
                    <td className="px-2 py-2 text-right font-medium text-gray-900">
                      {formatINR(calc?.total ?? 0)}
                    </td>
                    {/* Per-line GST mode chip — shows when it differs from doc mode */}
                    <td className="px-2 py-2">
                      {(() => {
                        const lineMode = item.gstMode ?? docGstMode;
                        const mismatch = lineMode !== docGstMode;
                        return (
                          <button
                            type="button"
                            title={mismatch ? `Line mode "${lineMode}" differs from document mode "${docGstMode}"` : `GST mode: ${lineMode}`}
                            onClick={() => updateLineItem(item.id, 'gstMode', lineMode === 'extra' ? 'inclusive' : 'extra')}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                              mismatch
                                ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-400'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {lineMode === 'extra' ? '+GST' : 'incl.'}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => removeLineItem(item.id)}
                        disabled={lineItems.length <= 1}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document-Level Discount */}
      <div className="mb-4 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4">
        <div className="flex items-center gap-3 mb-2">
          <h4 className="text-sm font-semibold text-amber-800">Proposal Discount</h4>
          <div className="flex rounded-lg border border-amber-300 overflow-hidden">
            <button onClick={() => setDocDiscountType('amount')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'amount' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
              &#8377; Amount
            </button>
            <button onClick={() => setDocDiscountType('percentage')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'percentage' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
              % Percentage
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            value={docDiscountValue || ''}
            onChange={e => setDocDiscountValue(parseFloat(e.target.value) || 0)}
            placeholder={docDiscountType === 'amount' ? 'Discount amount' : '% Discount'}
            className="w-48 rounded-lg border border-amber-300 px-3 py-2 text-sm"
          />
          <span className="text-sm text-amber-700">
            {docDiscountType === 'amount'
              ? `(${calculations.docDiscountPct?.toFixed(2) || 0}%)`
              : `(${formatINR(calculations.docDiscountAmt || 0)})`}
          </span>
          {(calculations.docDiscountPct ?? 0) > maxDocDiscountPct && (
            <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
              <AlertTriangle size={14} /> Exceeds max {maxDocDiscountPct}% — needs approval
            </span>
          )}
          {/* 🔒 SHOW the wall. Above it nothing can be approved. */}
          {allowanceExcludedLines.length > 0 ? (
            <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
              <AlertTriangle size={14} /> No allowance on this order &mdash; {allowanceExcludedLines.map(li => li.description.trim()).join(', ')}
            </span>
          ) : (
            <span className="text-xs text-amber-700">
              Allowance limit for this order: {fmtAllowancePct(additionalAllowanceLimitPct)}%
            </span>
          )}
        </div>
      </div>

      <ChargesSection charges={charges} onChange={setCharges} />

      {/* Service Prompts — auto-shown when non-service products exist */}
      {activeServicePrompts.length > 0 && (
        <div className="mb-4 space-y-3">
          {activeServicePrompts.map(term => {
            const config = term.conditionConfig || {};
            const options = (term.options || []) as TermOption[];
            const currentAnswer = servicePromptAnswers[term.termSlug] || '';
            return (
              <div key={term.id} className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">
                  {config.promptLabel || `Select ${term.termName}`}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {options.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => handleServicePromptSelect(term.termSlug, opt.key)}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                        currentAnswer === opt.key
                          ? 'border-blue-500 bg-blue-600 text-white'
                          : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {currentAnswer === 'quoted_separately' && (
                  <p className="mt-2 text-xs text-blue-600">
                    A {config.serviceProductName} line item has been added. Set the price in the items table above.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Inline Service Item Prompts — shown when a Service product is added directly */}
      {Object.entries(serviceItemPrompts).filter(([id, status]) => status === 'pending' && lineItems.some(li => li.id === id)).length > 0 && (
        <div className="mb-4 space-y-3">
          {Object.entries(serviceItemPrompts)
            .filter(([id, status]) => status === 'pending' && lineItems.some(li => li.id === id))
            .map(([itemId]) => {
              const item = lineItems.find(li => li.id === itemId);
              if (!item) return null;
              return (
                <div key={itemId} className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                  <h4 className="text-sm font-semibold text-indigo-800 mb-1">
                    This proposal includes {item.description}. Select terms:
                  </h4>
                  <p className="text-xs text-indigo-600 mb-3">How should this service be reflected in the proposal?</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleServiceItemPrompt(itemId, 'inclusive')}
                      className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      Inclusive
                    </button>
                    <button
                      type="button"
                      onClick={() => handleServiceItemPrompt(itemId, 'quoted_separately')}
                      className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      Quoted Separately
                    </button>
                    <button
                      type="button"
                      onClick={() => handleServiceItemPrompt(itemId, 'not_included')}
                      className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      Not Included
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Terms & Conditions */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <button
          type="button"
          onClick={() => setShowTermsSection(!showTermsSection)}
          className="flex w-full items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Terms & Conditions</h2>
            {selectedTermValues.length > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {selectedTermValues.length} terms
              </span>
            )}
            {detectedScope !== 'all' && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                {detectedScope}
              </span>
            )}
          </div>
          {showTermsSection ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {showTermsSection && (
          <div className="mt-4 space-y-4">
            {/* Template selector */}
            {termTemplates.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">T&C Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="w-full max-w-sm rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select a template...</option>
                  {termTemplates
                    .filter(t => t.scope === 'all' || t.scope === detectedScope || t.isDefault)
                    .map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} {t.isDefault ? '(Default)' : ''} {t.scope !== 'all' ? `[${t.scope}]` : ''}
                      </option>
                    ))}
                </select>
                {selectedTemplateId && (
                  <p className="mt-1 text-xs text-gray-400">
                    {termTemplates.find(t => t.id === selectedTemplateId)?.description || 'Override individual terms below as needed.'}
                  </p>
                )}
              </div>
            )}

            {/* Term rows */}
            {selectedTermValues.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-3 py-2 w-[30%]">Term</th>
                      <th className="px-3 py-2 w-[35%]">Selected Value</th>
                      <th className="px-3 py-2 w-[35%]">Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTermValues.map((tv) => {
                      const term = allTerms.find(t => t.termSlug === tv.termSlug);
                      const options = (term?.options as TermOption[]) || [];
                      const selectedOpt = options.find(o => o.key === tv.selectedOption);
                      return (
                        <tr key={tv.termSlug} className="border-b border-gray-100">
                          <td className="px-3 py-2">
                            <span className="font-medium text-gray-900">{tv.termName}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {tv.selectedOption === '__custom__' ? (
                              <span className="italic text-amber-600">Custom</span>
                            ) : (
                              selectedOpt?.label || tv.selectedOption || <span className="text-gray-400">Not set</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={tv.selectedOption}
                              onChange={(e) => handleTermOverride(tv.termSlug, e.target.value)}
                              className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                            >
                              <option value="">-- Select --</option>
                              {options.map(opt => (
                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                              ))}
                              {term?.allowCustom && (
                                <option value="__custom__">Custom value...</option>
                              )}
                            </select>
                            {tv.selectedOption === '__custom__' && (
                              <input
                                type="text"
                                value={tv.customValue || ''}
                                onChange={(e) => handleTermOverride(tv.termSlug, '__custom__', e.target.value)}
                                placeholder={term?.editableHint || 'Enter custom value'}
                                className="mt-1 w-full rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Show message if no terms loaded */}
            {allTerms.length === 0 && termTemplates.length === 0 && (
              <p className="text-sm text-gray-400 italic">
                No terms or templates configured yet. Add them via Proposal Builder settings.
              </p>
            )}

            {/* If terms exist but no template selected, show individual terms for manual selection */}
            {allTerms.length > 0 && selectedTermValues.length === 0 && (
              <div>
                <p className="mb-2 text-xs text-gray-500">No template selected. Add terms individually:</p>
                <button
                  type="button"
                  onClick={() => {
                    const scopeTerms = allTerms.filter(t => t.scope === 'all' || t.scope === detectedScope);
                    setSelectedTermValues(scopeTerms.map(term => {
                      const defaultOpt = (term.options as TermOption[])?.find(o => o.isDefault);
                      return {
                        termName: term.termName,
                        termSlug: term.termSlug,
                        selectedOption: defaultOpt?.key || '',
                      };
                    }));
                  }}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus size={14} className="mr-1 inline" />
                  Load All Terms with Defaults
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Notes</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Additional terms, conditions, or notes for the client..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
        />
      </div>

      {/* Summary */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Summary</h2>
        <div className="ml-auto w-full max-w-sm space-y-2">
          <SummaryRow label="Subtotal" value={formatINR(calculations.subtotal)} />
          {(calculations.docDiscountAmt ?? 0) > 0 && (
            <SummaryRow label={`Proposal Discount (${(calculations.docDiscountPct ?? 0).toFixed(1)}%)`} value={`-${formatINR(calculations.docDiscountAmt ?? 0)}`} />
          )}
          {/* Final review F2 (2026-08-20): the charges line. Without it the
              panel showed a goods-only tax figure beside a charges-inclusive
              Grand Total, so the total jumped with nothing explaining it.
              Charges sit AFTER the discount (never discounted) and outside
              Subtotal, so the panel now reads
              Subtotal − Discount + Charges + Total Tax = Grand Total. */}
          {(calculations.chargesSubtotal ?? 0) > 0 && (
            <SummaryRow label="Charges (ex-GST)" value={formatINR(calculations.chargesSubtotal)} />
          )}
          {buyerState && isIntraState && (
            <>
              <SummaryRow label="CGST" value={formatINR(calculations.totalCgst)} />
              <SummaryRow label="SGST" value={formatINR(calculations.totalSgst)} />
            </>
          )}
          {buyerState && !isIntraState && (
            <SummaryRow label="IGST" value={formatINR(calculations.totalIgst)} />
          )}
          {/* The tax figure carries goods tax + charge tax (see the convention
              note in the totals memo), so the label says so when it matters. */}
          <SummaryRow
            label={(calculations.chargesSubtotal ?? 0) > 0 ? 'Total Tax (incl. charges)' : 'Total Tax'}
            value={formatINR(buyerState ? calculations.totalTax : calculations.totalGst)}
          />
          {calculations.roundOff !== 0 && (
            <SummaryRow label="Round Off" value={formatINR(calculations.roundOff)} />
          )}
          <div className="border-t border-gray-200 pt-2">
            <div className="flex justify-between text-base font-bold text-gray-900">
              <span>Grand Total</span>
              <span>{formatINR(calculations.grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid Selector Modal */}
      <ProductGridSelector
        open={showProductGrid}
        onClose={() => setShowProductGrid(false)}
        onSelect={handleProductSelect}
      />

      {/* GST Mixed Mode Modal */}
      {gstMixedModalOpen && gstMixedInfo && (
        <GstModeMixedModal
          proposalId={id as string}
          documentMode={docGstMode}
          inclusiveCount={gstMixedInfo.inclusiveCount}
          extraCount={gstMixedInfo.extraCount}
          recommendation={gstMixedInfo.recommendation}
          onConvertAll={handleGstConvertAll}
          onSwitchDocMode={handleGstSwitchDocMode}
          onClose={() => setGstMixedModalOpen(false)}
        />
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
